import Autolinker from 'autolinker';
import { useTranslation } from 'react-i18next';
import { PillsList, RichTextEditor } from '@/pages/Schedule/components';
import clockIcon from '@/pages/Schedule/icons/clock.png';
import colorWheelIcon from '@/pages/Schedule/icons/color-wheel.png';
import labelIcon from '@/pages/Schedule/icons/label.png';
import pinIcon from '@/pages/Schedule/icons/pin.png';
import whoIcon from '@/pages/Schedule/icons/who.png';
import { useDraftEntryStore } from '@/pages/Schedule/stores';
import { getDateSummary, getEntryCategoryColors } from '@/pages/Schedule/utils';
import { Flex, Box, Link, Modal, VStack } from '@/ui';
import { isValidUrl } from '@/utils/url';
import {
  EntryModalBody,
  EntryModalContent,
  EntryModalField,
  EntryModalForm,
  EntryModalHeader,
} from '../components';
import { EntryModalRsvpBar } from '../components/EntryModalRsvpBar';
import { HEADER_HEIGHT, MODAL_WIDTH } from '../constants';

type Props = {
  onClose: () => void;
};

export const ReadOnlyEntryModal = ({ onClose }: Props) => {
  const { t } = useTranslation(['common', 'entryModal']);

  const instance = useDraftEntryStore((state) => state.draftInstance);
  if (!instance) {
    return null;
  }

  const colors = getEntryCategoryColors(instance.category?.color);
  const canRsvp = instance.rsvpSettings.enabled;

  return (
    <Modal isCentered isOpen onClose={onClose} onCloseComplete={onClose}>
      <EntryModalContent
        data-testid="read-only-entry-modal"
        h="auto"
        w={MODAL_WIDTH + 'px'}
      >
        <Flex direction="column" justify="space-between" w={MODAL_WIDTH + 'px'}>
          <EntryModalForm>
            <EntryModalHeader onSaveClick={onClose} />

            <EntryModalBody>
              {instance?.emoji && <Box fontSize="38px">{instance?.emoji}</Box>}

              <Box fontSize="2xl" mb="6">
                {instance?.title || t('common:untitled_entry')}
              </Box>

              <VStack
                alignItems="start"
                mb={instance?.description || instance?.notes ? 6 : 0}
                spacing="2"
              >
                <EntryModalField
                  icon={
                    <img alt="" height="18px" src={clockIcon} width="18px" />
                  }
                >
                  {getDateSummary(
                    instance.startDate,
                    instance.endDate,
                    instance.isOnDay,
                    instance.rule
                  )}
                </EntryModalField>
                {instance.category && (
                  <EntryModalField
                    icon={
                      <img
                        alt=""
                        height="18px"
                        src={colorWheelIcon}
                        width="18px"
                      />
                    }
                  >
                    <Box
                      bg={colors.backgroundColor}
                      borderRadius="4"
                      minW="150px"
                      px="2"
                      py="1.5"
                    >
                      {instance.category.text ?? <>&nbsp;</>}
                    </Box>
                  </EntryModalField>
                )}
                {instance.whoLabels.length > 0 && (
                  <EntryModalField
                    icon={
                      <img alt="" height="18px" src={whoIcon} width="18px" />
                    }
                  >
                    <Box>
                      <PillsList labels={instance.whoLabels} />
                    </Box>
                  </EntryModalField>
                )}
                {instance.labels.length > 0 && (
                  <EntryModalField
                    icon={
                      <img alt="" height="18px" src={labelIcon} width="18px" />
                    }
                  >
                    <Box>
                      <PillsList labels={instance.labels} />
                    </Box>
                  </EntryModalField>
                )}
                {instance?.locationWithPlace?.name && (
                  <EntryModalField
                    icon={
                      <img alt="" height="18px" src={pinIcon} width="18px" />
                    }
                  >
                    <Box>
                      <Link
                        target="_blank"
                        href={
                          isValidUrl(instance.locationWithPlace.name)
                            ? instance.locationWithPlace.name
                            : 'https://maps.google.com/maps?q=' +
                              encodeURIComponent(
                                instance.locationWithPlace.name
                              )
                        }
                      >
                        {instance.locationWithPlace.name}
                      </Link>
                    </Box>
                  </EntryModalField>
                )}
              </VStack>

              {instance?.description && (
                <RichTextEditor
                  readOnly
                  value={Autolinker.link(instance.description)}
                  containerProps={{
                    mb: `${instance?.notes ? 6 : 0}px`,
                    pb: `${instance?.notes ? 0 : HEADER_HEIGHT}px`,
                  }}
                />
              )}

              {instance?.notes && (
                <RichTextEditor
                  readOnly
                  value={Autolinker.link(instance.notes)}
                  containerProps={{
                    pb: `${HEADER_HEIGHT}px`,
                  }}
                />
              )}
            </EntryModalBody>
          </EntryModalForm>

          {canRsvp && <EntryModalRsvpBar />}
        </Flex>
      </EntryModalContent>
    </Modal>
  );
};
